import { Box, Button, Paper, useMediaQuery } from '@mui/material'
import Pagination from '@mui/material/Pagination'
import Typography from '@mui/material/Typography'
import React, { useCallback, useMemo, useState } from 'react'
import { Else, If, Then, When } from 'react-if'

import { useSearchContentQuery } from '../../../Queries/Common'
import { selectGames, useDictionariesQuery } from '../../../Queries/Dictionaries'
import { useOriginalMapsQuery } from '../../../Queries/Maps'
import { CONTENT_TYPES } from '../../../Scenes/constants'
import FAIcon from '../../../Theme/FAIcon'
import { FA_ICONS } from '../../../Theme/font-awesome-icon-constants'
import { BREAKPOINT_MIN_SM } from '../../../Theme/stylesUtil'
import { filtersNewTrendingFeatured } from '../../_common/constants'
import MapSkeletonWrapper from '../../_common/Skeletons/MapSkeletonWrapper'
import { GameFilter } from '../../Filter/GameFilter'
import SortControls from '../../FilterControls/SortControls'
import SearchField from '../../SearchComponents/SearchField'
import MapCardWrapper from './MapCardWrapper'

const DEFAULT_FILTER = { game: { id: 6, name: 'Halo Infinite' } }

const MapGridFavorites = ({
  emptyMaps,
  favoriteMaps,
  deleteMapCardFromFav,
  errors,
  isBiggerThanSMScreen,
  isOriginal,
}) => {
  const style = isBiggerThanSMScreen
    ? {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gridTemplateRows: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '24px',
        gridAutoFlow: 'dense',
      }
    : {
        backgroundColor: 'rgba(0, 0, 0, 0.15)',
        padding: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        borderRadius: '8px',
      }

  return (
    <Box sx={style}>
      <If condition={isBiggerThanSMScreen}>
        <Then>
          {favoriteMaps.map((map, index) => (
            <MapCardWrapper
              key={String(map?.id) + String(index)}
              map={map}
              style={{ height: '192px' }}
              deleteMapCardFromFav={deleteMapCardFromFav}
              favoriteMaps={favoriteMaps}
              target={'_blank'}
              isOriginal={isOriginal}
            />
          ))}
        </Then>
        <Else>
          {favoriteMaps.map((map, index) => (
            <Box
              key={String(map?.id) + String(index)}
              sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px' }}>
                <img
                  src={map?.map_images?.[0].urls?.['small']}
                  alt={map.title}
                  style={{ height: '40px', borderRadius: '8px' }}
                />
                <Typography
                  color="text.primary"
                  variant="h6"
                >
                  {map.title}
                </Typography>
              </Box>

              <Box>
                <Button
                  color={'error'}
                  onClick={() => deleteMapCardFromFav(map, isOriginal)}
                >
                  <FAIcon
                    iconClassName={FA_ICONS.CLOSE}
                    iconFontSize={14}
                  />
                </Button>
              </Box>
            </Box>
          ))}
        </Else>
      </If>
      <When condition={isBiggerThanSMScreen}>
        <MapSkeletonWrapper
          emptyMaps={emptyMaps}
          errors={errors}
        />
      </When>
    </Box>
  )
}

const ContestInvitationMapsComponent = ({
  errors,
  setHelperText,
  helperTexts,
  favoriteMaps,
  deleteMapCardFromFav,
  searchValue,
  saveMapCardToFav,
  isOriginal = false,
}) => {
  const { data: dictionaries } = useDictionariesQuery()
  const games = selectGames(dictionaries)
  const [filters, setFilters] = useState(DEFAULT_FILTER)
  const { data: originalMaps, isFetched: isFetchedOriginalMaps } = useOriginalMapsQuery(filters.game.id, isOriginal)
  const [sort, setSort] = useState('new')
  const [searchMap, setSearchMap] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const normalizedFilters = useMemo(
    () => ({
      game_id: filters.game.id,
      type: sort,
      searchValue: searchMap,
    }),
    [filters.game.id, searchMap, sort],
  )
  const { data: forgehubMaps, isFetched: isFetchedMaps } = useSearchContentQuery(
    { limit: 12, ...normalizedFilters, _page: currentPage },
    CONTENT_TYPES.MAPS,
    true,
    !isOriginal,
  )
  const error = isOriginal ? !!errors?.original_map_ids : !!errors?.favorite_map_ids
  const onMouseEnter = () => {
    if (!helperTexts) return
    isOriginal ? setHelperText(helperTexts.maps1) : setHelperText(helperTexts.maps2)
  }
  const handleAdd = useCallback(
    (filter, item) => {
      setFilters((prevFilters) => ({ ...prevFilters, [filter]: item }))
    },
    [setFilters],
  )
  const isBiggerThanSMScreen = useMediaQuery(BREAKPOINT_MIN_SM)
  const justifyContent = isOriginal ? 'end' : 'center'
  const isFetched = isOriginal ? isFetchedOriginalMaps : isFetchedMaps

  return (
    <Paper
      sx={{
        padding: 5,
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 2.5,
        gap: 4,
      }}
    >
      <Box onMouseEnter={onMouseEnter}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '20px' }}>
          <Typography
            variant="body2"
            color={error ? 'error.main' : 'text.tertiary'}
          >
            <If condition={isOriginal}>
              <Then>3 Favorite Official Halo maps</Then>
              <Else>4 Favorite 4v4 map from ForgeHub</Else>
            </If>
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {favoriteMaps.length}/{isOriginal ? '3' : '4'}
          </Typography>
        </Box>

        <Box>
          <MapGridFavorites
            emptyMaps={(isOriginal ? 3 : 4) - favoriteMaps.length}
            favoriteMaps={favoriteMaps}
            deleteMapCardFromFav={deleteMapCardFromFav}
            errors={errors}
            isBiggerThanSMScreen={isBiggerThanSMScreen}
            isOriginal={isOriginal}
          />
        </Box>
      </Box>

      <Box>
        <If condition={isBiggerThanSMScreen}>
          <Then>
            <Box sx={{ display: 'flex', justifyContent, alignItems: 'center', gap: '24px' }}>
              <When condition={!isOriginal}>
                <Box sx={{ flexBasis: '70%' }}>
                  <SearchField
                    value={searchValue}
                    onChange={setSearchMap}
                    allowClear
                    style={{ backdropFilter: 'blur(20px)' }}
                    placeholder={'Search'}
                  />
                </Box>
              </When>

              <Box sx={{ flexBasis: '30%', display: 'flex', justifyContent, alignItems: 'center', gap: '5px' }}>
                <GameFilter
                  filters={filters}
                  games={games}
                  handleAdd={handleAdd}
                />
                <When condition={!isOriginal}>
                  <SortControls
                    sort={sort}
                    onSort={setSort}
                    sortItems={filtersNewTrendingFeatured}
                  />
                </When>
              </Box>
            </Box>
          </Then>
          <Else>
            <Box sx={{ display: 'flex', justifyContent, alignItems: 'center', gap: '24px', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <GameFilter
                  filters={filters}
                  games={games}
                  handleAdd={handleAdd}
                />

                <When condition={!isOriginal}>
                  <SortControls
                    sort={sort}
                    onSort={setSort}
                    sortItems={filtersNewTrendingFeatured}
                  />
                </When>
              </Box>
              <When condition={!isOriginal}>
                <Box sx={{ width: '100%' }}>
                  <SearchField
                    value={searchValue}
                    onChange={setSearchMap}
                    allowClear
                    style={{ backdropFilter: 'blur(20px)' }}
                    placeholder={'Search'}
                  />
                </Box>
              </When>
            </Box>
          </Else>
        </If>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '24px',
            marginTop: '24px',
          }}
        >
          <If condition={!isFetched}>
            <Then>
              <MapSkeletonWrapper
                emptyMaps={12}
                animation={'wave'}
              />
            </Then>
            <Else>
              {isOriginal
                ? isFetched &&
                  originalMaps?.data.map((content, index) => (
                    <MapCardWrapper
                      key={String(content.id) + String(index)}
                      map={content}
                      saveMapCardToFav={saveMapCardToFav}
                      favoriteMaps={favoriteMaps}
                      deleteMapCardFromFav={deleteMapCardFromFav}
                      target={'_blank'}
                      isOriginal={isOriginal}
                    />
                  ))
                : isFetched &&
                  forgehubMaps?.pages?.map((page) =>
                    page.data.map((content, index) => (
                      <MapCardWrapper
                        key={String(content.id) + String(index)}
                        map={content}
                        saveMapCardToFav={saveMapCardToFav}
                        favoriteMaps={favoriteMaps}
                        deleteMapCardFromFav={deleteMapCardFromFav}
                        target={'_blank'}
                        isOriginal={isOriginal}
                      />
                    )),
                  )}
            </Else>
          </If>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '24px' }}>
          {!isOriginal && isFetched && forgehubMaps?.pages[0]?.pages > 1 && (
            <Pagination
              count={forgehubMaps?.pages[0]?.pages || 0}
              shape="rounded"
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
            />
          )}
        </Box>
      </Box>
    </Paper>
  )
}

export default ContestInvitationMapsComponent
